import type React from 'react'
import { useCallback } from 'react'
import { rotateDeltaToLocal } from '@/components/app/editor/tools/shared/helpers/rotate-delta-to-local'
import { toCanvasPoint } from '@/components/app/editor/tools/shared/helpers/svg-point'
import { anchorsToSvgPath } from '@/lib/elements/pen-path-utils'
import { deepClonePoints, shiftPointsBack } from '../helpers/anchor-points'
import { computeDraggedPoints } from '../helpers/drag-anchor-points'
import { expandToFitPoints } from '../helpers/expand-to-fit-points'
import type { NodeEditOverlayProps } from '../types/node-edit-overlay-props'
import type { DragTarget } from '../types/node-edit-targets'
import type { ModifierKeys } from './use-modifier-keys'
import type { NodeEditPropRefs } from './use-node-edit-prop-refs'
import type { NodePathState } from './use-node-path-state'

interface UseNodeDragHandlersOptions extends Pick<
  NodeEditOverlayProps,
  'onPathChange' | 'onPathCommit'
> {
  svgRef: React.RefObject<SVGSVGElement | null>
  path: NodePathState
  propRefs: NodeEditPropRefs
  modifierKeys: ModifierKeys
  shapeId: string
  bleedWidth: number
  bleedHeight: number
  geometryInteractionDisabled: boolean
}

/** Pointer handlers for dragging anchors and Bézier handles in select (non-pen) node-edit mode. */
export function useNodeDragHandlers({
  svgRef,
  path,
  propRefs,
  modifierKeys,
  shapeId,
  bleedWidth,
  bleedHeight,
  geometryInteractionDisabled,
  onPathChange,
  onPathCommit,
}: UseNodeDragHandlersOptions) {
  const { pointsRef, isClosedRef, subpathsRef, forceUpdate, dragRef, shapeGeomRef } = path
  const { selectedIndicesRef, multiSelectActiveRef } = path
  const { altHeldRef, shiftHeldRef } = modifierKeys

  const handleElementDown = useCallback(
    (e: React.PointerEvent, target: DragTarget) => {
      if (geometryInteractionDisabled) return
      if (e.button !== 0) return
      e.preventDefault()
      e.stopPropagation()
      ;(e.currentTarget as Element).setPointerCapture(e.pointerId)

      const { x, y } = toCanvasPoint(svgRef.current, e.clientX, e.clientY, bleedWidth, bleedHeight)

      // Update multi-selection on anchor click.
      // Shift+click adds the anchor to the selection (or leaves it if already selected —
      // removing on pointerdown would prevent shift+drag from moving the full selection).
      // Plain click resets to just this anchor, clearing any multi-selection.
      if (target.type === 'anchor') {
        const isShift = e.shiftKey || shiftHeldRef.current

        if (isShift) {
          multiSelectActiveRef.current = true
          if (!selectedIndicesRef.current.has(target.index)) {
            const next = new Set(selectedIndicesRef.current)

            next.add(target.index)
            selectedIndicesRef.current = next
            forceUpdate((n) => n + 1)
          }
          // Already selected: leave it in so the drag moves all selected nodes.
        } else {
          // Plain click on a node that is already part of the multi-selection: keep the
          // full selection so the drag moves all selected nodes together.
          // Plain click on an unselected node: reset to just that node (and drop out
          // of multi-select mode so it renders as a normal, unhighlighted anchor).
          if (!selectedIndicesRef.current.has(target.index)) {
            selectedIndicesRef.current = new Set([target.index])
            multiSelectActiveRef.current = false
            forceUpdate((n) => n + 1)
          }
        }
      }

      dragRef.current = {
        target,
        startCanvasX: x,
        startCanvasY: y,
        originalPoints: deepClonePoints(pointsRef.current),
        altKey: e.altKey,
        selectedIndices: new Set(selectedIndicesRef.current),
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bleedWidth, bleedHeight]
  )

  const handleElementMove = useCallback(
    (e: React.PointerEvent) => {
      const drag = dragRef.current

      if (!drag) return
      e.stopPropagation()

      const { x: cx, y: cy } = toCanvasPoint(
        svgRef.current,
        e.clientX,
        e.clientY,
        bleedWidth,
        bleedHeight
      )

      // The pointer delta is in canvas (screen) space. Node coordinates live in the
      // shape's pre-rotation local space. Applying the inverse rotation converts the
      // canvas delta into local space so nodes move in the visually expected direction.
      const { dx, dy } = rotateDeltaToLocal(
        cx - drag.startCanvasX,
        cy - drag.startCanvasY,
        propRefs.shapeRotationRef.current
      )

      const { points: updated, createdHandles } = computeDraggedPoints(drag, dx, dy, {
        shift: e.shiftKey || shiftHeldRef.current,
        alt: e.altKey || altHeldRef.current,
      })

      if (createdHandles) drag.creatingHandles = true

      const expanded = expandToFitPoints(
        updated,
        shapeGeomRef.current,
        isClosedRef.current,
        subpathsRef.current
      )
      const finalPts = expanded ? expanded.pts : updated

      if (expanded) {
        // The normalization shifted all local coordinates by (-shiftX, -shiftY).
        // Update the drag baseline by the same amount so future dx/dy deltas stay correct.
        const shiftX = expanded.geom.x - shapeGeomRef.current.x
        const shiftY = expanded.geom.y - shapeGeomRef.current.y

        drag.originalPoints = shiftPointsBack(drag.originalPoints, shiftX, shiftY)
        shapeGeomRef.current = expanded.geom
      }

      pointsRef.current = finalPts
      forceUpdate((n) => n + 1)
      onPathChange(
        shapeId,
        anchorsToSvgPath(finalPts, isClosedRef.current, subpathsRef.current),
        expanded ? expanded.geom : undefined
      )
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [shapeId, bleedWidth, bleedHeight, onPathChange]
  )

  const handleElementUp = useCallback(
    (e: React.PointerEvent) => {
      const drag = dragRef.current

      if (!drag) return
      e.stopPropagation()

      let finalPoints = pointsRef.current

      if (drag.creatingHandles && drag.target.type === 'anchor') {
        const updated = deepClonePoints(finalPoints)

        updated[drag.target.index] = { ...updated[drag.target.index], type: 'smooth' }
        pointsRef.current = updated
        finalPoints = updated
        forceUpdate((n) => n + 1)
      }

      dragRef.current = null
      onPathCommit(shapeId, anchorsToSvgPath(finalPoints, isClosedRef.current, subpathsRef.current))
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [shapeId, onPathCommit]
  )

  return { handleElementDown, handleElementMove, handleElementUp }
}
